import { load as parseYaml } from "js-yaml";
import MarkdownIt from "markdown-it";
import DOMPurify from "dompurify";

// Minimal frontmatter split (gray-matter depends on Node's Buffer, which
// doesn't exist in the browser at runtime) — everything between the first
// pair of `---` lines is YAML, the rest is the Markdown body.
function splitFrontmatter(raw) {
  const m = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/.exec(raw);
  if (!m) return { data: {}, content: raw };
  return { data: parseYaml(m[1]) || {}, content: m[2] };
}

// Articles are authored as Markdown files with frontmatter under
// content/articles/*.md. This loads them at build time (import.meta.glob is
// resolved statically by Vite) and reshapes them into the same flat object
// shape the rest of the app (useNetdoc.js, all components) already expects,
// so nothing downstream needs to change.
const files = import.meta.glob("/content/articles/*.md", {
  eager: true,
  query: "?raw",
  import: "default",
});

const md = new MarkdownIt({ html: false, linkify: true, breaks: false });

export function loadArticles() {
  const articles = Object.entries(files).map(([path, raw]) => {
    const id = path.split("/").pop().replace(/\.md$/, "");
    const { data, content } = splitFrontmatter(raw);

    // The design's template reads `intro` (plain lead paragraph) and
    // `summary` (the "en bref" blockquote) as separate strings — derive
    // them back out of the markdown body so existing components keep working
    // unchanged. Anything written *after* the blockquote is the fiche's real
    // body copy, rendered separately as `bodyHtml` (Article.jsx's "Fiche"
    // tab, below the "En bref" box) — captured on its own so it isn't
    // rendered twice over (once manually via intro/summary, once via
    // bodyHtml) for the common case of a fiche that's only an intro + quote.
    const paraMatch = content.trim().match(/^([\s\S]*?)\n\n>\s?([\s\S]*?)(?:\n\n([\s\S]*))?$/);
    const intro = (paraMatch ? paraMatch[1] : content.trim()).trim();
    const summary = (paraMatch ? paraMatch[2] : "").trim();
    const rest = (paraMatch && paraMatch[3] ? paraMatch[3] : "").trim();
    const bodyHtml = rest ? DOMPurify.sanitize(md.render(rest)) : "";

    return {
      id,
      short: data.short,
      domain: data.domain,
      title: data.title,
      meta: data.meta,
      table: !!data.table,
      pitfalls: data.pitfalls || [],
      cli: data.cli || {},
      snippet: data.snippet || "",
      intro,
      summary,
      bodyHtml,
    };
  });

  // Deterministic order (glob already returns paths alphabetically); the
  // index screen applies its own title sort, so this only affects prev/next
  // article cycling on the article view.
  articles.sort((a, b) => a.id.localeCompare(b.id));
  return articles;
}
